import { ComponentProps, useMemo, useRef, useState } from "react";
import { useController, useFormContext } from "react-hook-form";
import locale from "@bonliva-crm/locale";
import { SingleSelect } from "@bonliva-crm/ui";
import { useUpdateEffect } from "@hooks";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";
import { isFilterSelected } from "../utils/is-filter-selected";
import { SingleValueChip } from "./_select/single-value-chip";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";

export type QualificationFilterData =
  | "all"
  | "showQualified"
  | "showExpired"
  | "showUnqualified";

type Option = { label: string; value: string };

export type QualificationFilterProps = FilterWithFavouriteProps;

export const QualificationFilter = (props: QualificationFilterProps) => {
  const options = useMemo(() => {
    return [
      { label: locale.ShowQualified, value: "showQualified" },
      { label: locale.ShowExpired, value: "showExpired" },
      { label: locale.ShowUnqualified, value: "showUnqualified" }
    ];
  }, []);

  const selectRef = useRef(null);
  const { setValue, resetField } = useFormContext();
  const { field, fieldState } = useController({ name: props.name });
  const [expanded, setExpanded] = useState(fieldState.isDirty);
  const title = props.label ?? locale.QualificationFilter;
  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const clear = () => resetField(props.name);

  const value = {
    label: options.find((option) => option.value === field?.value)?.label,
    value: field?.value
  };

  const commonProps: ComponentProps<typeof SingleSelect<Option>> = {
    options,
    name: props.name,
    value,
    onChange: (selected) => {
      setValue(props.name, selected.value as QualificationFilterData, {
        shouldDirty: true
      });
    },
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: false,
    maxMenuHeight: 600,
    showBadge: isFilterSelected(field?.value),
    optionType: "radio",
    isClearable: isFilterSelected(field?.value),
    onClear: () => setValue(props.name, null, { shouldDirty: true })
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <SingleSelect<Option>
          {...commonProps}
          selectRef={selectRef}
          isClearable={false}
          controlShouldRenderValue
          openMenuOnFocus
          menuPortalTarget={document.body}
          showBadge={false}
          placeholder={locale.SelectValue}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
          components={{
            SingleValue: (p) => (
              <SingleValueChip {...p} onDelete={() => resetField(props.name)} />
            )
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  } else {
    return (
      <SingleSelect<Option>
        {...commonProps}
        styleProps={{
          control: { showBorder: true },
          menu: { showBorder: true }
        }}
      />
    );
  }
};
